import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Dialog, DialogTitle, AppBar, Toolbar, IconButton, Typography, Icon, TextField, InputAdornment, List, ListItem, Collapse, ListItemText, ListItemIcon, DialogContent, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useStyles, { listStyles } from './ScoreTag.styles';
import cpUseStyles from '../../ChecklistScores/ChecklistScores.styles';
import { get, size, first } from 'global/lib/lodash';
import { Loader } from 'global/styles';
import sendData from 'redux/actions/sendData';
import { Alert } from '@mui/material';
import clsx from 'clsx';
import gstyles from 'global/styles';
import getHighlightedText from 'global/components/highlight-text';
import useDebounce from 'global/hooks/useDebounce';
import { filterData } from '../utils';
import ScoreIcon from 'global/components/ScoreIcon';
import { CenterLoader, useScreen } from '../../../../global/styles';
import { Row } from '../../../../global/components/row';
import Switch from '../../../../global/components/switch';
import { Text } from '../../../../global/components/text';
import { ObservationCollapse, createSubScore, deleteSubsOnCheckpoint } from './ObservationCreate';
import { useElement } from '../../../../global/hooks/useIntersect';
import { stringObj } from '../../../../global/utils';
import { IconButton as IcoButton } from '../../../../global/components/Button';

export const AUTOTAG = '__AUTO__'

const System = (props) => {
  const styles = listStyles();
  const [open, setOpen] = useState(!!props.allOpen || get(props, 'alwaysOpen', false));

  const { system, search } = props;
  const title = get(system, 'name', '');
  const checklists = get(system, 'children', []);
  const clhaschildren = checklists.find(c => size(c.children));

  useEffect(() => {
    if (search) {
      setOpen(true);
    } else {
      setOpen(!!props.allOpen || get(props, 'alwaysOpen', false));
    }
  }, [system]);

  return !size(checklists) || !clhaschildren ? null : (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)}>
        <ListItemText primary={title} primaryTypographyProps={{ variant: 'button' }} />
        <ListItemIcon>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      <Collapse in={open} unmountOnExit>
        <List disablePadding className={styles.marginLeft}>
          {checklists.map((check, i) => (
            <Checklist {...props} key={get(check, 'checklistId')} checklist={check} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const Checklist = (props) => {
  const styles = listStyles();
  const [open, setOpen] = useState(get(props, 'allOpen', false));

  const { system, checklist, search } = props;
  const title = get(checklist, 'name', '');

  const checkpoints = get(checklist, 'children', []).filter((c) => c.checkpointId);

  useEffect(() => {
    const isMatch = get(props, 'tag.checklist.id') == get(checklist, 'checklistId') || get(props, 'tag.checklist.checklistId') == get(checklist, 'checklistId');
    if (search || isMatch) {
      setOpen(true);
    } else {
      setOpen(get(props, 'allOpen', false));
    }
  }, [checklist]);

  const tagMe = useCallback((checkpoint) => {
    props.changeTag({
      system,
      checklist,
      checkpoint,
    });
  }, []);

  return !size(checkpoints) ? null : (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)} id={checklist.checklistId}>
        <ListItemText primary={title} />
        <ListItemIcon className={styles.marginRight}>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      {!props.thinking && (
        <Collapse in={open}>
          <List disablePadding>
            {checkpoints.map((check) => (
              <Checkpoint key={`${get(props, 'room.id', '')}${get(check, "checkpointId")}`} checkpoint={check} onClick={tagMe} {...props} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const Checkpoint = (props) => {
  const { checkpoint, search, isMobile } = props;
  const [thinking, setThinking] = useState(false);
  const [autos, setautos] = useState([]);
  
  const relevantSubs = get(checkpoint, 'subscores', []).filter(c => c.realmId == get(props, 'room.id'));

  const [scored, setScored] = useState([]);

  useEffect(() => {
    setScored(!!size(relevantSubs));
    setautos(relevantSubs.filter(f => get(f, 'notes') == AUTOTAG))
  }, [size(relevantSubs)]);

  const hasCriteria = get(checkpoint, 'criteria');
  const classes = cpUseStyles();
  // This is checking both to see if the scorecard uses room-checkpoints (represented by `allOpen`)
  // And to check if the interface even has the function to operate (`submitEmptySubscore`) -- which excludes the admin interface
  const showAutoScore = get(props, 'allOpen', false) && !!get(props, 'submitEmptySubscore', false);

  const selectCheckpoint = () => {
    props.onClick({
      checkpoint,
    });
  };

  const nonautos = relevantSubs.filter(f => get(f, 'notes') != AUTOTAG);
  const visibleSubSize = size(nonautos);

  const isScored = scored;
  const hasAutos = !!size(autos);
  const isDisabled = isScored && visibleSubSize;

  const createEmptySubscore = async () => {
    try {
      setThinking(true);
      const willDelete = hasAutos || isScored;
      if (willDelete) {
        await deleteSubsOnCheckpoint({ ...checkpoint, realmId: get(props, 'room.id') });
      } else {
        await props.submitEmptySubscore(checkpoint)
      }
      if (willDelete) {
        setautos([]);
      }
    } catch (err) {} finally {
      setThinking(false);
      setScored(isScored ? false : true);
    }
  }

  return (
    <div className={classes.topic}>
      <div className={classes.topicFlex}>
        <ListItem button onClick={selectCheckpoint} style={{ paddingLeft: 0 }}>
          <div className={classes.rowTop}>
            <div style={{ marginRight: 8 }}>
              <ScoreIcon selected={false} center isAddMenu score={!isScored ? null : 0} />
            </div>
            <Typography variant="body2">
              <strong>{getHighlightedText(checkpoint.name || '', search)}</strong>
              &nbsp;
              {getHighlightedText(hasCriteria || '', search)}
            </Typography>
          </div>
        </ListItem>
        {showAutoScore && <IcoButton onClick={createEmptySubscore} disabled={isDisabled} loading={thinking} variant="none" icon={scored ? "close" : "check"} loadingProps={{ size: 16 }} />}
      </div>
      {!!visibleSubSize && <div style={{ marginLeft: isMobile ? 36 : 90 }}>
        <ObservationCollapse checkpoint={{ ...checkpoint, subscores: nonautos }} />
      </div>}
    </div>
  );
};

export default forwardRef((props, forRef) => {
  const { allScoreData, scorecard, updateScores } = props;

  const [error, setError] = useState();
  const [tags, setTags] = useState({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const [thinking, setThinking] = useState(false);
  const [treed, setTreed] = useState();
  const [state, setState] = useState({
    useCps: true,
  })

  const [ref, searchView] = useElement();

  const { isMobileXS } = useScreen();

  const roomcps = (() => {
    if (props.room && get(scorecard, 'config.roomCheckpoints')) {
      const obj = get(scorecard, 'config.roomCheckpoints', []).find(r => r.id == props.room.id);
      return !size(get(obj, 'cps')) ? undefined : obj;
    }
  })();

  const runFilter = async (d = allScoreData) => {
    try {
      setThinking(true);
      const { hasChildren } = await filterData(d, { search: debouncedSearch, value: 'any', roomcps: roomcps && state.useCps ? roomcps : undefined }, false);
      setTreed(hasChildren);
    } catch (err) {
      console.warn(err);
    } finally {
      setThinking(false);
    }
  };

  useEffect(() => {
    if (props.open) {
      runFilter();
    }
  }, [debouncedSearch, allScoreData, props.open, props.room, state.useCps]);

  const showToggle = !!roomcps && !!search;
  const topVal = isMobileXS ? 16 : 24;
  const classes = useStyles({ showToggle, isMobile: isMobileXS, searchView, topVal });
  const styles = gstyles();
  const showSearch = get(props, 'showSearch', true);

  const handleClose = () => {
    props.onClose();
  };

  const reset = () => {
    setTags({});
    setSearch('');
    setThinking(false);
  };

  const changeTag = useCallback((tag) => {
    setTags(tag);
  }, []);

  const searchChange = (val) => {
    setSearch(val);
    setThinking(true);
    if (val === '') {
      setState(s => ({ ...s, useCps: true }))
    }
  };

  const shouldCloseAndReset = get(props, 'closeAndReset', true);

  useEffect(() => {
    if (Object.keys(tags).length > 0) {
      props.onTagSelected && props.onTagSelected({
        ...tags,
        // scorecardId,
      });

      if (shouldCloseAndReset) {
        props.onClose();
        reset();
      }
    }
  }, [tags]);

  const submitEmptySubscore = async (checkpoint) => {
    try {
      setThinking(true);
      let realm = get(props, 'room');
  
      const isNewrealm = typeof realm === 'string' || realm instanceof String;
      if (isNewrealm) {
        const result = await sendData(
          {
            url: `/realms`,
            method: 'post',
            data: {
              data: {
                type: 'realms',
                attributes: {
                  name: realm,
                  tier: get(props,'rooms.realmFilter.tier', 'room'),
                  parent_id: get(first(get(props,'rooms.data',[])), 'parentId') || get(checkpoint, 'assessmentRealmId', undefined),
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );
  
        get(props,'rooms.update', () => 0)();
        realm = first(result);
      }
  
      const subscore = get(checkpoint, '_subscore');
  
      const data = {
        data: {
          type: 'sub-scores',
          id: get(subscore, 'id'),
          attributes: stringObj({
            superset_id: get(checkpoint, 'supersetId', ''),
            assessment_id: get(checkpoint, 'assessmentId', ''),
            scorecard_id: get(checkpoint, 'scorecardId', ''),
            checklist_id: get(checkpoint, 'checklistId', ''),
            checkpoint_id: get(checkpoint, 'checkpointId', ''),
            checkpoint_option_id: undefined,
            scorecard_option_id: undefined,
            score: "100",
            criteria: get(checkpoint, 'criteria', ''),
            realm_id: get(realm, 'id'),
            notes: AUTOTAG,
            images: [],
          }),
        },
      };
  
      await createSubScore(data, { removeAutos: false });
    } catch (err) {
      alert(err);
    } finally {
      setThinking(false);
    }
  };

  useEffect(() => {
    const hasTagId = get(props, 'tag.checklist.checklistId', get(props, 'tag.checklist.id'));
    if (props.open && !!hasTagId) {
      setTimeout(() => {
        const elem = document.getElementById(hasTagId);
        elem && elem.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, [props.open])

  const showBlank = !size(treed); //size(textMatches.term) > 0 && !size(textMatches.systems);

  useImperativeHandle(forRef, () => ({
    searchChange,
  }), []);

  return (
    <Dialog className={classes.dialog} disableEscapeKeyDown open={props.open} onClose={handleClose} {...get(props, 'dialogProps', {})}>
      {!!props.renderTitleBar && <DialogTitle classes={{ root: classes.dialogTitle }}>
        <AppBar color="secondary" className={classes.appBar}>
          <Toolbar className={classes.toolbar}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => handleClose()}
              aria-label="Close"
              size="large">
              <Icon>close</Icon>
            </IconButton>

            {props.renderTitleBar && props.renderTitleBar()}

            {!!props.endIcon && <Icon onClick={props.endIconClick} style={{ marginLeft: 'auto', cursor: props.endIconClick ? 'pointer' : 'defautl' }}>{props.endIcon}</Icon>}
          </Toolbar>
        </AppBar>
      </DialogTitle>}
      <DialogContent>
        <div className={classes.container}>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          <div className={classes.inputContainer}>
            <Typography id='tagSubtitle' variant="subtitle2" className={classes.subtitle}>
              {get(props, 'subTitle', 'Link To Criteria')}
            </Typography>

            {props.open && showSearch && <div className={classes.searchContainer} ref={ref}>
              <TextField
                variant="outlined"
                fullWidth
                id="search"
                value={search}
                label="Search"
                onChange={(e) => searchChange(e.target.value)}
                inputRef={(input) => input && !isMobileXS && input.focus()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment disablePointerEvents={!search} position="end">
                      <IconButton onClick={() => searchChange('')} size="large">{thinking ? <Loader size={20} /> : <Icon>{search ? 'close' : 'search'}</Icon>}</IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {showToggle && <Row>
                <Text>{`Searching "${get(props, 'room.name')}". Show all criteria?`}</Text><Switch checked={!state.useCps} onChange={() => setState(s => ({ ...s, useCps: !s.useCps }))} />
              </Row>}
            </div>}

            <div className={classes.list}>
              
              {!!showBlank && (
                <div className={styles.centerColumn} style={{ padding: 20 }}>
                  <span>{`There are no results that meet your search criteria.`}</span>
                </div>
              )}

              {/* {!showBlank && thinking && <CenterLoader />} */}

              {!showBlank && !!props.open && (
                <List component="nav">
                  {get(treed, '0.children', []).map((sys, i) => (
                    <System
                      key={i}
                      tag={props.tag}
                      changeTag={changeTag}
                      system={sys}
                      search={debouncedSearch}
                      // Need to only set if scores can be updated (i.e. not in the admin interface where it's a preview)
                      submitEmptySubscore={ !!get(props,'updateScores') ? submitEmptySubscore : undefined }
                      // thinking={thinking}
                      scorecardConfig={get(scorecard, 'config', {})}
                      allOpen={roomcps && get(props, 'rooms.data.0.tier') == 'room'}
                      alwaysOpen={size(get(treed, '0.children', [])) === 1 || sys.supersetId == get(props, 'tag.system.id')}
                      isMobile={isMobileXS}
                      room={props.room}
                    />
                  ))}
                </List>
              )}

              {props.renderFooter && props.renderFooter()}
            </div>

            
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});
